import { useCallback, forwardRef, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  removeAddressImage,
  selectEmployeeInformation,
  setAddressImage,
  setSameAsPermanent,
  updateAddressField,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./AddressDetails.module.scss";

const states = [
  "Select",
  "ANDHRA PRADESH",
  "ARUNACHAL PRADESH",
  "ASSAM",
  "BIHAR",
  "CHHATTISGARH",
  "GOA",
  "GUJARAT",
  "HARYANA",
  "HIMACHAL PRADESH",
  "JHARKHAND",
  "KARNATAKA",
  "KERALA",
  "MADHYA PRADESH",
  "MAHARASHTRA",
  "MANIPUR",
  "MEGHALAYA",
  "MIZORAM",
  "NAGALAND",
  "ODISHA",
  "PUNJAB",
  "RAJASTHAN",
  "SIKKIM",
  "TAMIL NADU",
  "TELANGANA",
  "TRIPURA",
  "UTTAR PRADESH",
  "UTTARAKHAND",
  "WEST BENGAL",
];

const requiredFields = [
  // Permanent Address
  { name: "street_name1", label: "Permanent Street name" },
  { name: "city_name1", label: "Permanent City" },
  { name: "state_name1", label: "Permanent State" },
  { name: "pin_code1", label: "Permanent Pincode" },

  // Present Address
  { name: "street_name", label: "Present Street name" },
  { name: "city_name", label: "Present City" },
  { name: "state_name", label: "Present State" },
  { name: "pin_code", label: "Present Pincode" },
];

const AddressDetails = forwardRef(function AddressDetails(_, ref) {
  const dispatch = useDispatch();

  const {
    addressDetails: form,
    sameAsPermanent,
    addressImages,
  } = useSelector(selectEmployeeInformation);

  const aadhaarBackImage = addressImages.aadhaarBack;

  const [errorFields, setErrorFields] = useState(new Set());

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(updateAddressField({ name, value }));
    clearError(name);
  };

  const updateTextField = (name, value) => {
    updateField(name, value.toUpperCase());
  };

  const updatePincode = (name, value) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    updateField(name, numericValue);
  };

  const handleImageChange = (file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    if (aadhaarBackImage?.url) {
      URL.revokeObjectURL(aadhaarBackImage.url);
    }

    dispatch(
      setAddressImage({
        name: "aadhaarBack",
        image: {
          name: file.name,
          url: URL.createObjectURL(file),
        },
      }),
    );

    clearError("aadhaarBack");
  };

  const removeImage = () => {
    if (aadhaarBackImage?.url) {
      URL.revokeObjectURL(aadhaarBackImage.url);
    }

    dispatch(removeAddressImage("aadhaarBack"));
  };

  const handleSameAsPermanent = (event) => {
    const checked = event.target.checked;

    dispatch(setSameAsPermanent(checked));

    if (checked) {
      // Copy permanent address into present address
      updateField("street_name", form.street_name1 || "");
      updateField("town_name", form.town_name1 || "");
      updateField("city_name", form.city_name1 || "");
      updateField("state_name", form.state_name1 || "Select");
      updateField("pin_code", form.pin_code1 || "");
      updateField("state_id", form.state_id1 || "");
    }
  };

  const getMissingFields = useCallback(() => {
    const missingObjects = requiredFields.filter((field) => {
      const value = form[field.name] || "";

      if (field.name === "state_name" || field.name === "state_name1") {
        return !value || value === "Select";
      }

      if (field.name === "pin_code" || field.name === "pin_code1") {
        return value.length !== 6;
      }

      return value.trim().length === 0;
    });

    const missingNames = new Set(missingObjects.map((f) => f.name));

    if (!aadhaarBackImage) {
      missingNames.add("aadhaarBack");
    }

    setErrorFields(missingNames);

    const missingLabels = missingObjects.map((f) => f.label);

    if (!aadhaarBackImage) {
      missingLabels.push("Aadhaar Backside image");
    }

    return missingLabels;
  }, [aadhaarBackImage, form]);

  useImperativeHandle(
    ref,
    () => ({
      getMissingFields,
      isComplete: () => getMissingFields().length === 0,
    }),
    [getMissingFields],
  );

  const renderAddressFields = (type, disabled = false) => {
    const isPermanent = type === "permanent";

    const fieldNames = isPermanent
      ? {
          street: "street_name1",
          town: "town_name1",
          city: "city_name1",
          state: "state_name1",
          pincode: "pin_code1",
          stateId: "state_id1",
        }
      : {
          street: "street_name",
          town: "town_name",
          city: "city_name",
          state: "state_name",
          pincode: "pin_code",
          stateId: "state_id",
        };

    console.log("address", form);

    return (
      <div className={styles.addressGrid}>
        <label className={styles.field}>
          <span>
            Street name<b>*</b>
          </span>
          <textarea
            value={form[fieldNames.street] || ""}
            disabled={disabled}
            className={
              errorFields.has(fieldNames.street) ? styles.inputError : ""
            }
            onChange={(event) =>
              updateTextField(fieldNames.street, event.target.value)
            }
          />
          {errorFields.has(fieldNames.street) && (
            <p className={styles.fieldError}>Please fill this field</p>
          )}
        </label>

        <label className={styles.field}>
          <span>Town</span>
          <input
            value={form[fieldNames.town] || ""}
            disabled={disabled}
            onChange={(event) =>
              updateTextField(fieldNames.town, event.target.value)
            }
          />
        </label>

        <label className={styles.field}>
          <span>
            City<b>*</b>
          </span>
          <input
            value={form[fieldNames.city] || ""}
            disabled={disabled}
            className={
              errorFields.has(fieldNames.city) ? styles.inputError : ""
            }
            onChange={(event) =>
              updateTextField(fieldNames.city, event.target.value)
            }
          />
          {errorFields.has(fieldNames.city) && (
            <p className={styles.fieldError}>Please fill this field</p>
          )}
        </label>

        <label className={styles.field}>
          <span>
            State<b>*</b>
          </span>
          <select
            value={form[fieldNames.state] || "Select"}
            disabled={disabled}
            className={
              errorFields.has(fieldNames.state) ? styles.inputError : ""
            }
            onChange={(event) => {
              updateField(fieldNames.state, event.target.value);
              updateField(fieldNames.stateId, event.target.value);
            }}
          >
            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          {errorFields.has(fieldNames.state) && (
            <p className={styles.fieldError}>Please select a state</p>
          )}
        </label>

        <label className={styles.field}>
          <span>
            Pincode<b>*</b>
          </span>
          <input
            value={form[fieldNames.pincode] || ""}
            disabled={disabled}
            inputMode="numeric"
            maxLength={6}
            pattern="[0-9]*"
            className={
              errorFields.has(fieldNames.pincode) ? styles.inputError : ""
            }
            onChange={(event) =>
              updatePincode(fieldNames.pincode, event.target.value)
            }
          />
          {errorFields.has(fieldNames.pincode) && (
            <p className={styles.fieldError}>
              Please enter a valid 6-digit pincode
            </p>
          )}
        </label>
      </div>
    );
  };

  return (
    <div className={styles.basicInformation}>
      <header className={styles.sectionHeader}>Address Details</header>

      <div className={styles.content}>
        <p className={styles.note}>
          Note: Upload the Aadhaar Back picture. Verify that the file is in JPG,
          JPEG, or PNG format and that its size is less than 10 MB.
        </p>

        <div className={styles.uploadGridSingle}>
          <div className={styles.uploadBlock}>
            <label className={styles.label}>
              Aadhaar Backside image<span>*</span>
            </label>

            <div className={styles.previewRow}>
              {aadhaarBackImage ? (
                <>
                  <img
                    className={styles.imagePreview}
                    src={aadhaarBackImage.url}
                    alt="Aadhaar backside preview"
                  />
                  <button
                    className={styles.removeButton}
                    type="button"
                    onClick={removeImage}
                  >
                    Remove Image
                  </button>
                </>
              ) : (
                <label className={styles.uploadButton}>
                  Upload Image
                  <input
                    className={styles.hiddenInput}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={(event) =>
                      handleImageChange(event.target.files?.[0])
                    }
                  />
                </label>
              )}
            </div>

            {errorFields.has("aadhaarBack") && (
              <p className={styles.fieldError}>Please upload this image</p>
            )}
          </div>
        </div>

        <h2 className={styles.subsectionTitle}>Permanent Address</h2>
        {renderAddressFields("permanent")}

        <label className={styles.sameAddress}>
          <input
            type="checkbox"
            checked={sameAsPermanent}
            onChange={handleSameAsPermanent}
          />
          <span>Same as Permanent Address</span>
        </label>

        <h2 className={styles.subsectionTitle}>Present Address</h2>
        {renderAddressFields("present", sameAsPermanent)}
      </div>
    </div>
  );
});

export default AddressDetails;
// ```

// ---

// ## Matching Redux State Shape

// ```javascript
// export const initialAddressDetails = {
//   // Present Address
//   street_name: "",
//   town_name: "",
//   city_name: "",
//   state_name: "Select",
//   pin_code: "",
//   state_id: "",

//   // Permanent Address
//   street_name1: "",
//   town_name1: "",
//   city_name1: "",
//   state_name1: "Select",
//   pin_code1: "",
//   state_id1: "",

//   // Additional
//   aadhar_number: "",
// };
